import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sun, Zap, PoundSterling, Battery, Home, MapPin, Loader2, CheckCircle2, Ruler, TrendingUp, Satellite, Compass } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import RoofMap from "../components/RoofMap";
import { BATTERY_OPTIONS, BATTERY_LOCATIONS, calcEstimate } from "../lib/solar";
import { submitSolarEstimate, fetchSolarInsights } from "../lib/api";

const inputClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3.5 font-figtree focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all";
const labelClass = "block text-sm font-medium text-stone-700 mb-1.5 font-figtree";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAPS_CONFIGURED = API_KEY && API_KEY !== "YOUR_GOOGLE_MAPS_API_KEY";

const Section = ({ n, title, icon: Icon, children }) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8">
    <div className="flex items-center gap-3 mb-6">
      <span className="w-10 h-10 rounded-xl bg-brand-cream border border-stone-200 flex items-center justify-center"><Icon className="w-5 h-5 text-brand-accent" /></span>
      <h3 className="font-outfit text-xl font-medium text-brand"><span className="text-brand-accent">{n}.</span> {title}</h3>
    </div>
    {children}
  </div>
);

export default function SolarCalculator() {
  const [roofArea, setRoofArea] = useState(0);
  const [polygon, setPolygon] = useState([]);
  const [solarData, setSolarData] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [batteryKey, setBatteryKey] = useState("medium");
  const [batteryLocation, setBatteryLocation] = useState("");
  const [occupancy, setOccupancy] = useState("");
  const [usageType, setUsageType] = useState("bill");
  const [monthlyBill, setMonthlyBill] = useState("");
  const [annualUsageKwh, setAnnualUsageKwh] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", address: "" });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // manual drawing overrides satellite auto-detection
  const onArea = (area, pts) => {
    setRoofArea(area);
    setPolygon(pts || []);
    if (area > 0) setSolarData(null);
  };
  const setC = (k, v) => setContact((c) => ({ ...c, [k]: v }));

  const onPlaceSelected = async (lat, lng, address) => {
    setDetecting(true);
    setSolarData(null);
    if (address) setContact((c) => ({ ...c, address }));
    try {
      const d = await fetchSolarInsights(lat, lng);
      if (d.found && d.bestPanelCount > 0) {
        setSolarData({
          panelCount: d.bestPanelCount,
          panelCapacityWatts: d.panelCapacityWatts,
          annualGeneration: d.bestYearlyEnergyKwh,
          roofAreaM2: d.roofAreaM2,
          maxSunshineHours: d.maxSunshineHours,
          orientationDegrees: d.orientationDegrees,
          maxPanels: d.maxPanels,
        });
        setRoofArea(d.roofAreaM2 || 0);
        toast.success("Roof auto-detected from satellite data!");
      } else {
        toast.info("No satellite roof data here — please draw your roof on the map instead.");
      }
    } catch {
      toast.info("Couldn't auto-detect — please draw your roof on the map instead.");
    } finally {
      setDetecting(false);
    }
  };

  const estimate = useMemo(
    () => calcEstimate({ roofArea, batteryKey, monthlyBill, annualUsageKwh, usageType, solarData }),
    [roofArea, batteryKey, monthlyBill, annualUsageKwh, usageType, solarData]
  );

  const calculate = () => {
    if ((!roofArea || roofArea < 4) && !solarData) { toast.error("Search your address or draw your roof on the map first."); return; }
    setResult(estimate);
    setTimeout(() => document.getElementById("solar-result")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const saveLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await submitSolarEstimate({
        ...contact,
        occupancy,
        roof_area_m2: roofArea,
        panel_count: estimate.panelCount,
        system_kwp: estimate.systemKwp,
        annual_generation_kwh: estimate.annualGeneration,
        battery_option: batteryKey,
        battery_location: batteryLocation || null,
        usage_input_type: usageType,
        monthly_bill: usageType === "bill" && monthlyBill ? Number(monthlyBill) : null,
        annual_usage_kwh: usageType === "kwh" && annualUsageKwh ? Number(annualUsageKwh) : null,
        estimated_cost: estimate.estimatedCost,
        annual_savings: estimate.annualSavings,
        payback_years: estimate.paybackYears,
        roof_polygon: polygon,
      });
      setSaved(true);
      toast.success("Estimate saved! Our team will be in touch to confirm.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="solar-calculator-page">
      <PageHeader
        overline="Solar Calculator"
        title="Estimate your solar system in minutes"
        subtitle="Pinpoint your roof on the map, choose a battery, tell us about your usage — and get an instant, no-obligation solar & savings estimate."
      />

      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-6">
          {/* 1. Roof */}
          <Reveal>
            <Section n="1" title="Find & measure your roof" icon={MapPin}>
              {MAPS_CONFIGURED ? (
                <RoofMap apiKey={API_KEY} onArea={onArea} area={roofArea} onPlaceSelected={onPlaceSelected} />
              ) : (
                <div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-800 font-figtree">
                    The satellite roof map isn't connected yet (Google Maps API key pending). You can still get an estimate by entering your approximate roof area below.
                  </div>
                  <label className={labelClass}>Approximate usable roof area (m²)</label>
                  <input data-testid="roof-area-manual" type="number" min="0" value={roofArea || ""} onChange={(e) => setRoofArea(Number(e.target.value))} className={inputClass} placeholder="e.g. 30" />
                </div>
              )}

              {detecting && (
                <div className="mt-4 flex items-center gap-2 font-figtree text-sm text-stone-600">
                  <Loader2 className="w-4 h-4 animate-spin text-brand-accent" /> Analysing your roof from satellite imagery…
                </div>
              )}

              {solarData && (
                <div data-testid="solar-detected-banner" className="mt-4 bg-brand-success/10 border border-brand-success/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Satellite className="w-4 h-4 text-brand-success" />
                    <p className="font-outfit font-medium text-brand text-sm">Roof auto-detected from satellite data</p>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 font-figtree text-sm text-stone-600">
                    <span>Roof area: <b className="text-brand">{solarData.roofAreaM2} m²</b></span>
                    <span>Max panels that fit: <b className="text-brand">{solarData.maxPanels}</b></span>
                    {solarData.maxSunshineHours ? <span>Sunshine: <b className="text-brand">{solarData.maxSunshineHours.toLocaleString()} hrs/yr</b></span> : null}
                    {typeof solarData.orientationDegrees === "number" ? <span className="inline-flex items-center gap-1"><Compass className="w-3.5 h-3.5" /> {orientationLabel(solarData.orientationDegrees)}</span> : null}
                  </div>
                  <p className="font-figtree text-xs text-stone-500 mt-2">Prefer to measure it yourself? Just draw on the map to override.</p>
                </div>
              )}

              <div className="mt-4 flex items-center gap-2 font-figtree text-sm">
                <Ruler className="w-4 h-4 text-brand-accent" />
                <span className="text-stone-600">Measured roof area:</span>
                <span data-testid="roof-area-value" className="font-semibold text-brand">{roofArea ? `${roofArea} m²` : "—"}</span>
              </div>
            </Section>
          </Reveal>

          {/* 2. Battery */}
          <Reveal>
            <Section n="2" title="Choose a battery" icon={Battery}>
              <div className="grid sm:grid-cols-2 gap-3">
                {BATTERY_OPTIONS.map((b) => {
                  const active = batteryKey === b.key;
                  return (
                    <button type="button" key={b.key} data-testid={`battery-${b.key}`} onClick={() => setBatteryKey(b.key)}
                      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-brand-accent bg-brand-accent/5 ring-2 ring-brand-accent" : "border-stone-200 hover:border-brand"}`}>
                      <p className="font-outfit font-medium text-brand">{b.label}</p>
                      <p className="font-figtree text-sm text-stone-500 mt-0.5">{b.desc}</p>
                      {b.cost > 0 && <p className="font-figtree text-sm text-brand-accent font-medium mt-1">+£{b.cost.toLocaleString()}</p>}
                    </button>
                  );
                })}
              </div>
              {batteryKey !== "none" && (
                <div className="mt-5">
                  <label className={labelClass}>Where would the battery go?</label>
                  <select data-testid="battery-location-select" value={batteryLocation} onChange={(e) => setBatteryLocation(e.target.value)} className={inputClass}>
                    <option value="">Select a location…</option>
                    {BATTERY_LOCATIONS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </div>
              )}
            </Section>
          </Reveal>

          {/* 3. About you */}
          <Reveal>
            <Section n="3" title="About you & your usage" icon={Home}>
              <label className={labelClass}>Are you the property owner or a tenant?</label>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Owner", "Tenant", "Landlord"].map((o) => (
                  <button type="button" key={o} data-testid={`occupancy-${o.toLowerCase()}`} onClick={() => setOccupancy(o)}
                    className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${occupancy === o ? "bg-brand text-white border-brand" : "bg-white text-brand border-stone-300 hover:border-brand"}`}>
                    {o}
                  </button>
                ))}
              </div>

              <label className={labelClass}>How would you like to enter your electricity usage?</label>
              <div className="flex gap-2 mb-4">
                <button type="button" data-testid="usage-toggle-bill" onClick={() => setUsageType("bill")} className={`rounded-full px-5 py-2.5 text-sm font-medium border ${usageType === "bill" ? "bg-brand-accent text-white border-brand-accent" : "bg-white text-brand border-stone-300"}`}>Monthly bill (£)</button>
                <button type="button" data-testid="usage-toggle-kwh" onClick={() => setUsageType("kwh")} className={`rounded-full px-5 py-2.5 text-sm font-medium border ${usageType === "kwh" ? "bg-brand-accent text-white border-brand-accent" : "bg-white text-brand border-stone-300"}`}>Annual usage (kWh)</button>
              </div>
              {usageType === "bill" ? (
                <input data-testid="usage-bill-input" type="number" min="0" value={monthlyBill} onChange={(e) => setMonthlyBill(e.target.value)} className={inputClass} placeholder="Average monthly electricity bill, e.g. 120" />
              ) : (
                <input data-testid="usage-kwh-input" type="number" min="0" value={annualUsageKwh} onChange={(e) => setAnnualUsageKwh(e.target.value)} className={inputClass} placeholder="Annual usage in kWh, e.g. 3500" />
              )}
            </Section>
          </Reveal>

          <div className="text-center">
            <button data-testid="calculate-button" onClick={calculate} className="bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-9 py-4 font-medium inline-flex items-center gap-2 text-lg">
              <Sun className="w-5 h-5" /> Calculate my estimate
            </button>
          </div>

          {/* Result */}
          {result && (
            <div id="solar-result" data-testid="solar-result" className="scroll-mt-28">
              <Reveal>
                <div className="bg-brand text-brand-cream rounded-2xl p-8 relative overflow-hidden">
                  <div className="absolute inset-0 grain-overlay" />
                  <div className="relative">
                    <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-4">Your indicative estimate</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                      <Stat icon={Sun} value={result.panelCount} label="Solar panels" />
                      <Stat icon={Zap} value={`${result.systemKwp} kWp`} label="System size" />
                      <Stat icon={TrendingUp} value={`${result.annualGeneration.toLocaleString()}`} label="kWh / year generated" />
                      <Stat icon={PoundSterling} value={`£${result.annualSavings.toLocaleString()}`} label="Est. annual saving" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 font-figtree text-sm">
                      <Pill label="Battery" value={result.battery.label} />
                      <Pill label="Indicative cost" value={`£${result.estimatedCost.toLocaleString()}`} />
                      <Pill label="Payback" value={result.paybackYears ? `~${result.paybackYears} yrs` : "—"} />
                    </div>
                    <p className="font-figtree text-xs text-brand-cream/50 mt-6">These figures are indicative only and depend on roof orientation, shading and a full survey. With no upfront cost options, this can be funded through finance and any available grants.</p>
                  </div>
                </div>
              </Reveal>

              {/* Save lead */}
              <Reveal>
                {saved ? (
                  <div data-testid="solar-saved" className="bg-white border border-stone-200 rounded-2xl p-10 text-center mt-6">
                    <CheckCircle2 className="w-14 h-14 text-brand-success mx-auto mb-4" />
                    <h3 className="font-outfit text-2xl font-semibold text-brand mb-2">Estimate saved</h3>
                    <p className="font-figtree text-stone-600">Thanks {contact.name.split(" ")[0]}! Our team will review your solar estimate and contact you to arrange a free, no-obligation survey.</p>
                  </div>
                ) : (
                  <form data-testid="solar-lead-form" onSubmit={saveLead} className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 mt-6 space-y-5">
                    <h3 className="font-outfit text-xl font-medium text-brand">Get your full quote — no upfront cost</h3>
                    <p className="font-figtree text-sm text-stone-600 -mt-3">Leave your details and we'll confirm your estimate and check grant & finance eligibility.</p>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div><label className={labelClass}>Full name</label><input data-testid="solar-name-input" required value={contact.name} onChange={(e) => setC("name", e.target.value)} className={inputClass} placeholder="Jane Smith" /></div>
                      <div><label className={labelClass}>Email</label><input data-testid="solar-email-input" type="email" required value={contact.email} onChange={(e) => setC("email", e.target.value)} className={inputClass} placeholder="jane@email.com" /></div>
                      <div><label className={labelClass}>Phone</label><input data-testid="solar-phone-input" required value={contact.phone} onChange={(e) => setC("phone", e.target.value)} className={inputClass} placeholder="07123 456789" /></div>
                      <div><label className={labelClass}>Address / postcode</label><input data-testid="solar-address-input" value={contact.address} onChange={(e) => setC("address", e.target.value)} className={inputClass} placeholder="Property address" /></div>
                    </div>
                    <button data-testid="solar-save-button" type="submit" disabled={saving} className="w-full bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                      {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</> : "Send me my full quote"}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label }) => (  <div>
    <Icon className="w-6 h-6 text-brand-accent mb-2" />
    <p className="font-outfit text-2xl sm:text-3xl font-bold">{value}</p>
    <p className="font-figtree text-sm text-brand-cream/60">{label}</p>
  </div>
);

const Pill = ({ label, value }) => (
  <div className="bg-brand-dark/60 border border-brand-success/20 rounded-xl px-4 py-3">
    <p className="text-brand-cream/50 text-xs uppercase tracking-wider">{label}</p>
    <p className="font-outfit font-medium text-brand-cream mt-0.5">{value}</p>
  </div>
);


function orientationLabel(deg) {
  const dirs = ["North", "North-East", "East", "South-East", "South", "South-West", "West", "North-West"];
  const idx = Math.round(((deg % 360) / 45)) % 8;
  return `${dirs[idx]}-facing`;
}
